
import JSZip from "jszip";
import { saveAs } from 'file-saver';
import config from "../../../config";

export default async function shareImages(answers, caseData, intl) {
  const imageUrls = getImageUrlsFromAnswers(answers);
  const autoNumbering = isUsingAutoNumbering(answers);
  const filePrefix = intl.formatMessage({ id: 'formRenderer.downloadImages.figure' });
  const zip = new JSZip();

  imageUrls
    .map(imageObj => ({ ...imageObj, downloadUrl: imageObj.uneditedUrl ? imageObj.uneditedUrl : imageObj.url }))
    .map((imageObj, index) => ({
      promise: fetchImageBlob(imageObj.downloadUrl),
      filename: getFileName(imageObj, autoNumbering, index, filePrefix)
    }))
    .forEach(b => zip.file(b.filename, b.promise));

  const zipFile = await zip.generateAsync({ type: "blob" });

  const filename = cleanFileName(`${caseData.id} ${getCaseDomain(caseData, intl)} ${intl.formatMessage({ id: 'formRenderer.downloadImages.figures' })}`);
  saveAs(zipFile, `${filename}.zip`);

  return zipFile;
}

function getFileName(imageObj, isUsingAutoNumbering, index, filePrefix) {
  let number = isUsingAutoNumbering ? imageObj.autoNumber : imageObj.number;
  if (!number) {
    number = index + 1;
  } else {
    number = cleanFileName(number.toString());
  }
  const extension = getFileExtension(imageObj.downloadUrl);
  return `${filePrefix} ${number}.${extension}`;
}

function getFileExtension(downloadUrl) {
  if (!downloadUrl) {
    return "jpg";
  }

  if (downloadUrl.startsWith("data:")) {
    const match = downloadUrl.match(/^data:(image\/[^;]+);/i);
    if (match && match[1]) {
      return match[1].split("/").pop();
    }
  }

  const urlWithoutQuery = downloadUrl.split("?")[0].split("#")[0];
  const lastSegment = urlWithoutQuery.split("/").pop() || "";
  const parts = lastSegment.split(".");
  if (parts.length > 1) {
    return parts.pop();
  }

  return "jpg";
}

async function fetchImageBlob(downloadUrl) {
  if (!downloadUrl) {
    throw new Error("Missing image URL");
  }

  if (isApiImageUrl(downloadUrl)) {
    const presignedUrl = await getPresignedUrl(downloadUrl);
    const presignedResponse = await fetch(presignedUrl);
    if (!presignedResponse.ok) {
      throw new Error(`Presigned image download failed (${presignedResponse.status})`);
    }
    return presignedResponse.blob();
  }

  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error(`Image download failed (${response.status})`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.startsWith("image/")) {
    return response.blob();
  }

  const text = await response.text();
  const urlFromText = extractUrlFromText(text);
  if (urlFromText) {
    const redirectResponse = await fetch(urlFromText);
    if (!redirectResponse.ok) {
      throw new Error(`Redirect image download failed (${redirectResponse.status})`);
    }
    return redirectResponse.blob();
  }

  const dataUrlMatch = text.match(/^data:(image\/[^;]+);base64,(.+)$/i);
  if (dataUrlMatch) {
    return base64ToBlob(dataUrlMatch[2], dataUrlMatch[1]);
  }

  if (looksLikeBase64(text)) {
    const fallbackType = contentType.startsWith("image/") ? contentType : "image/jpeg";
    return base64ToBlob(text, fallbackType);
  }

  throw new Error("Unexpected image response content");
}

function isApiImageUrl(downloadUrl) {
  if (!config.API_URL) {
    return false;
  }

  return downloadUrl.startsWith(config.API_URL) && downloadUrl.includes("/image/");
}

async function getPresignedUrl(downloadUrl) {
  const url = new URL(downloadUrl);
  url.searchParams.set("presign", "1");

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Presign request failed (${response.status})`);
  }

  const payload = await response.json();
  if (payload && typeof payload.url === "string") {
    return payload.url;
  }

  throw new Error("Presign response missing url");
}

function extractUrlFromText(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) {
    return null;
  }

  const unquoted = trimmed.replace(/^"|"$/g, "");
  if (unquoted.startsWith("http://") || unquoted.startsWith("https://")) {
    return unquoted;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object") {
      if (typeof parsed.url === "string") {
        return parsed.url;
      }
      if (typeof parsed.location === "string") {
        return parsed.location;
      }
    }
  } catch (error) {
    // ignore
  }

  return null;
}

function looksLikeBase64(text) {
  const normalized = (text || "").trim();
  if (!normalized || normalized.length % 4 !== 0) {
    return false;
  }
  return /^[A-Za-z0-9+/=\s]+$/.test(normalized);
}

function base64ToBlob(base64, contentType) {
  const cleaned = base64.replace(/\s/g, "");
  const byteCharacters = atob(cleaned);
  const byteArrays = [];
  const sliceSize = 512;

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: contentType });
}

function cleanFileName(filename) {
  return filename.replace(/[/\\?%*:|"<>]/g, '')
}

export function getImageUrlsFromAnswers(answers) {
  return answers
    .reduce((acc, item) => {
      const urls = getImageUrlsFromAnswer(item.value, item.name).filter(imageToAdd => !acc.find(image => image.url === imageToAdd.url))
      return [...acc, ...urls];
    }, []);
}

function getImageUrlsFromAnswer(answervalue, answername) {
  if (answervalue?.includes("imageUrls")) {
    const value = JSON.parse(answervalue);
    if (value.imageUrls) {
      return value.imageUrls.filter(image => image.url && image.url !== "").map(image => ({ ...image, name: answername }));
    }
  }
  return [];
}

function isUsingAutoNumbering(answers) {
  const answer = answers.find(a => a.name === "reportDetails.AutoFigureNumbering");
  return answer && answer.value === "true";
}

function getCaseDomain(caseData, intl) {
  const languageKey = getLanguageKey(caseData);
  if (languageKey) {
    return intl.formatMessage({ id: languageKey })
  } else {
    return caseData.domain;
  }
}

function getLanguageKey(caseData) {
  // checking three ids for each report
  // because there is a specific id for earch report type on dev/staging/prod
  switch (caseData.formtemplateId) {
    case 56:
    case 44:
    case 20:
      return "case.formHeader.bearingDamageAnalysis";
    case 133:
    case 32:
    case 46:
      return "navigation.gbi";
    case 170:
    case 87:
    case 73:
      return "bearingInspection.livePreview.header";
    case 218:
    case 131:
    case 128:
      return "usBearingInspection.livePreview.header2";
    case 200:
    case 99:
    case 101:
      return "case.formHeader.wind";
    default:
      return undefined;
  }
}

